import { useLingui } from '@lingui/react/macro'
import { NavigateFn, useSearch } from '@tanstack/react-router'
import { Column, ColumnDef, ColumnFiltersState } from '@tanstack/react-table'
import { Button } from 'designSystem/components/Button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from 'designSystem/components/InputGroup'
import { Search } from 'lucide-react'
import React from 'react'
import styled, { css } from 'styled-components'

import { ColumnConfiguration } from './ColumnConfiguration'

const StyledFilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 2.5rem;
`

const FilterGroupStyles = css`
  display: flex;
  gap: 4px;
  height: 100%;
  &:empty {
    display: none;
  }
`
const FilterButtonGroup = styled.div`
  ${FilterGroupStyles}
  min-width: 0;
  width: 100%;
`

const SearchContainer = styled.div`
  ${FilterGroupStyles}
  margin-left: auto;
`

export const TableTopBar = <TData,>({
  columns,
  setGlobalFilter,
  getColumn,
  showGlobalFilter,
  showColumnConfig,
  leadingContent,
  navigate,
  disableUrlFilters,
  defaultVisibleColumnIds,
  bulkEditEnabled,
  onClickBulkEdit,
  tableName,
}: {
  columns: ColumnDef<TData>[]
  setGlobalFilter: React.Dispatch<unknown>
  getColumn: (columnID: string) => Column<TData, unknown> | undefined
  showFilterBar: boolean
  showGlobalFilter: boolean
  showColumnConfig: boolean
  leadingContent?: React.ReactNode
  initialColumnFilters?: ColumnFiltersState
  navigate: NavigateFn
  clearAll?: () => void
  disableUrlFilters?: boolean
  defaultVisibleColumnIds?: string[]
  bulkEditEnabled?: boolean
  onClickBulkEdit?: () => void
  tableName?: string
}) => {
  const { t } = useLingui()
  const search = useSearch({ strict: false })
  const searchParam = search.search as string
  const [searchValue, setSearchValue] = React.useState(searchParam || '')

  React.useEffect(() => {
    setSearchValue(searchParam || '')
    setGlobalFilter(searchParam || '')
  }, [searchParam, setGlobalFilter])

  const handleFilterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget
    setSearchValue(value)
    setGlobalFilter(value)
    if (!disableUrlFilters) {
      navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          search: value || undefined,
        }),
      })
    }
  }

  return (
    <StyledFilterContainer>
      {leadingContent ? (
        <FilterButtonGroup>{leadingContent}</FilterButtonGroup>
      ) : null}
      {bulkEditEnabled && (
        <FilterButtonGroup>
          <Button
            variant='raised'
            size='lg'
            style={{ width: '7rem' }}
            onClick={onClickBulkEdit}>
            {t`Bulk Actions`}
          </Button>
        </FilterButtonGroup>
      )}
      {showGlobalFilter && (
        <SearchContainer>
          <InputGroup>
            <InputGroupInput
              type='search'
              placeholder={t`Search`}
              value={searchValue}
              onChange={handleFilterInputChange}
              aria-label={tableName ? t`Search ${tableName}` : t`Search`}
            />
            <InputGroupAddon align='inline-start' aria-hidden='true'>
              <Search />
            </InputGroupAddon>
          </InputGroup>
          {showColumnConfig && (
            <ColumnConfiguration
              columns={columns}
              getColumn={getColumn}
              defaultVisibleColumnIds={defaultVisibleColumnIds}
            />
          )}
        </SearchContainer>
      )}
    </StyledFilterContainer>
  )
}
